import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {combineLatest, Observable} from 'rxjs';
import {User} from '@services/model/user';
import {VoilaUserService} from '@services/voila-user.service';
import {map, share} from 'rxjs/operators';
import {MnetUserResource} from '@services/resource/user-resource';
import {Title} from '@services/resource/title';
import {UserType} from '@services/resource/user-type';
import {UserPrivilege} from '@services/resource/user-privilege';
import {UUID} from '@util/types';
import {Startup} from '@services/model/startup';
import {HandelspartnerResource} from '@services/resource/handelspartner-resource';
import {MarkantDialog, MarkantElementsModule} from '@markant/elements-angular';
import {MatCard, MatCardActions, MatCardContent, MatCardTitle} from '@angular/material/card';
import {CommonModule} from '@angular/common';
import {MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatInput} from '@angular/material/input';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatHint,
    MatCardActions,
    MatInput,
  ],
})
export class EditUserComponent implements OnInit {
  Title = Title;
  UserType = UserType;
  UserPrivilege = UserPrivilege;
  @Input() form: HandelspartnerResource | null;
  dialogState: boolean;
  @Output() closeEvent = new EventEmitter<boolean>();
  @ViewChild('UserDialog') userDialog: MarkantDialog;

  userForm = new UntypedFormGroup({
    email: new UntypedFormControl(),
    title: new UntypedFormControl(),
    firstName: new UntypedFormControl(),
    lastName: new UntypedFormControl(),
    userType: new UntypedFormControl(),
    phone: new UntypedFormControl(),
    permissions: new UntypedFormControl(),
    companyId: new UntypedFormControl(),
    mnetId: new UntypedFormControl(),
    startup: new UntypedFormControl(),
    lastSeen: new UntypedFormControl(),
    userPrivilege: new UntypedFormControl(),
  });

  user$: Observable<User>;
  mnetUser$: Observable<MnetUserResource>;
  differences$: Observable<UserDiff>;

  constructor(private readonly userService: VoilaUserService) {}

  ngOnInit() {
    this.dialogState = true;
    const mnetId = this.form?.mnetId as string;
    this.user$ = this.userService.read(mnetId, true).pipe(share());

    this.mnetUser$ = this.userService.readFromMnet(mnetId).pipe(share());

    this.user$.subscribe((u) => {
      this.userForm.patchValue(u);
    });

    this.differences$ = combineLatest([this.user$, this.mnetUser$]).pipe(
      map((x) => diffUser(...x)),
      share(),
    );
  }

  save(userId: UUID): void {
    const user = this.userForm.value as User;
    this.userService.saveUser(userId, user).subscribe();
    this.userDialog.open = false;
    this.closeEvent.emit(false);
  }

  cancel() {
    this.userDialog.open = false;
    this.closeEvent.emit(false);
  }

  hasToBeDisabled() {
    if (this.userService.currentUser().userType != UserType.MA) {
      return true;
    }
    if (this.userService.currentUser().id == this.userForm.get('id')?.value) {
      return true;
    }
    return this.userForm.get('userType')?.value != 'MA';
  }
}

function diffUser(u: User, mnet: MnetUserResource): UserDiff {
  return {
    email: diff(u.email, mnet.email),
    firstName: diff(u.firstName, mnet.firstName),
    lastName: diff(u.lastName, mnet.lastName),
    title: diff(u.title, mnet.title),
    phone: diff(u.phone, mnet.phone),
  };
}

function diff<A>(current: A, original: A): A | undefined {
  if (current === original) {
    return undefined;
  } else {
    return original;
  }
}

interface UserDiff {
  email?: string;
  title?: Title;
  firstName?: string;
  lastName?: string;
  userType?: UserType;
  phone?: string;
  permissions?: string[];
  companyId?: UUID;
  mnetId?: string;
  startup?: Startup;
  lastSeen?: Date;
  userPrivilege?: UserPrivilege;
}
